import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Braces, Settings2, X } from 'lucide-react'
import { mergeClasses } from '@/helpers/styling'
import { assertNever } from '@/utils/assert-never'
import { MiniButton } from '@/components/ui-primitives/small'
import { SimpleTooltip } from '@/components/ui-primitives/simple-tooltip'
import { useElementRect, VisibleBox, widgetLayer } from './widget-outline-overlay'
import { useWidgetSelectorStore } from './widget-selector'
import { closeQueryOverlay, openQueryOverlay, openWidgetSettings, useQueryOverlayStore } from './query-navigation'
import { QueryOverlay } from './query-overlay'
import { useWidgetQueryStatusStore, WidgetQueryStatus } from '@/components/editor/widget-editor/query-status'
import { useLocalSettings } from '@/components/editor/local-settings'
import { useConfigMode } from '@/components/editor/configuration-mode'
import { usePanelRegistry } from '@/components/editor/panels/panel-registry'
import { findAreaElementForPath } from '@/components/editor/widget-dom'
import { hideSettingsOnWidget, useSettingsOnWidgetStore, WIDGET_EDIT_PANEL } from '@/components/editor/widget-editor/settings-size'
import { useSelector } from '@/store'
import { useHostedViewerCtx } from '@/hooks/use-app-ctx'

const CHIP_STYLE = {
  success: "border-[#51479e]/40 bg-white0 text-[#51479e] hover:bg-[#51479e] hover:text-white",
  pending: "border-border bg-white0 text-muted-foreground hover:text-foreground",
  error: "border-[#9b1c1c]/40 bg-[#fde8e8] text-[#9b1c1c]",
  disabled: "border-[#6b4600]/30 bg-[#fff4dc] text-[#6b4600]",
} as const

const CHIP = "flex items-center gap-1 whitespace-nowrap rounded-md border-[1px] p-1 font-exo text-[11px] leading-none shadow-sm"
const CHIP_OPEN = "border-[#51479e] bg-[#51479e] text-white"

/** Half the chip's height: it straddles the widget's top edge */
const CHIP_HALF_HEIGHT = 10

/**
 * Chips on the selected widget, and while picking on the widget under the mouse: `{ } SPARQL` when it
 * runs a query, opening that query on top of the widget, and Settings, opening its settings.
 */
export function QueryChips() {
  const statuses = useWidgetQueryStatusStore()
  const selectedPath = useWidgetSelectorStore(s => s.selectedPath)
  const overlayPath = useQueryOverlayStore(s => s.path)
  const overlaySize = useQueryOverlayStore(s => s.size)
  const settingsOnWidget = useSettingsOnWidgetStore(s => s.onWidget && s.shown)
  const panelShown = usePanelRegistry(r => r.openPanels.has(WIDGET_EDIT_PANEL) || r.floatingPanels.includes(WIDGET_EDIT_PANEL))
  const isEditMode = useConfigMode() === "edit"
  // while picking, the widget under the mouse gets chips too; otherwise only the selected one
  const [ isPicking, setIsPicking ] = useState(false)
  const pickHoveredPath = useWidgetSelectorStore(s => s.hoveredPath)
  const hoveredPath = isPicking ? pickHoveredPath : null
  const widgetSelector = useHostedViewerCtx().widgetSelectorRef
  // a chip click picks its widget, so it ends picking like a click on the widget would
  const stopPicking = () => {
    if (widgetSelector.current?.started()) widgetSelector.current.cancel()
  }
  // re-resolve the widgets' elements when the config changes them
  useSelector(s => s.currentViewer.config)

  useEffect(function followPicking() {
    const selector = widgetSelector.current
    if (!selector) return
    setIsPicking(selector.started())
    selector.startedSignal.connect(setIsPicking)
    return () => selector.startedSignal.disconnect(setIsPicking)
  }, [ widgetSelector ])

  useEffect(function closeOverlayWhenAnotherWidgetIsSelected() {
    if (overlayPath && overlayPath !== selectedPath) closeQueryOverlay()
  }, [ overlayPath, selectedPath ])

  if (!isEditMode) return null

  const queryOverlayOnWidget = overlayPath != null && overlaySize === "overlay"
  const chipPaths = new Set([ hoveredPath, selectedPath, overlayPath ].filter((path): path is string => !!path))
  const widgets = [ ...chipPaths ].flatMap(path => {
    const target = findAreaElementForPath(path)
    if (!target) return []
    const status = statuses[target.dataset.widgetId ?? ""]
    return [ { path, target, status: hasQuery(status) ? status : null } ]
  })
  const overlayWidget = widgets.find(w => w.path === overlayPath && w.status)
  const anyQuery = Object.values(statuses).some(hasQuery)

  return (
    <>
      {widgets.map(({ path, target, status }) => (
        <WidgetChips key={path}
                     target={target}
                     path={path}
                     status={status}
                     selected={path === selectedPath}
                     queryOpen={path === overlayPath && overlaySize === "overlay"}
                     settingsOpen={settingsOnWidget && !panelShown && !queryOverlayOnWidget && path === selectedPath}
                     settingsInPanel={panelShown && path === selectedPath}
                     onPick={stopPicking} />
      ))}
      {anyQuery && <QueryChipsHint />}
      {overlayWidget?.status && (
        <QueryOverlay key={overlayWidget.path}
                      target={overlayWidget.target}
                      path={overlayWidget.path}
                      status={overlayWidget.status}
                      size={overlaySize} />
      )}
    </>
  )
}

function WidgetChips(props: {
  target: HTMLElement
  path: string
  status: WidgetQueryStatus | null
  selected: boolean
  queryOpen: boolean
  settingsOpen: boolean
  /** The "Edit widgets" panel already shows this widget, so the settings chip would do nothing */
  settingsInPanel: boolean
  onPick: () => void
}) {
  const { status } = props
  const rect = useElementRect(props.target)
  const query = status && describe(status)
  // the chips' full width, measured before paint, to drop their text when the widget is narrower
  const groupRef = useRef<HTMLDivElement>(null)
  const [ fullWidth, setFullWidth ] = useState<number | null>(null)
  useLayoutEffect(function remeasureWhenLabelChanges() {
    setFullWidth(null)
  }, [ query?.short ])
  const isRendered = !!rect
  useLayoutEffect(function measureFullWidth() {
    if (fullWidth == null && groupRef.current) setFullWidth(groupRef.current.offsetWidth)
  }, [ fullWidth, isRendered ])
  // widgets in a hidden tab or collapsed panel have no size
  if (!rect || !rect.width || !rect.height) return null

  const box = visiblePart(rect, rect.visible)
  // keep the chips clear of the top bar and anything else the widget scrolls under
  const chipTop = Math.max(rect.top, rect.visible.top + CHIP_HALF_HEIGHT + 2)
  if (!box || chipTop > box.bottom - CHIP_HALF_HEIGHT) return null

  // the widget type label sits in the top left corner, so leave it room
  const labelWidth = 7 * (props.target.dataset.widgetType?.length ?? 7) + 24
  const compact = fullWidth != null && fullWidth > box.right - box.left - 16 - labelWidth
  const layer = widgetLayer(props.target)
  return createPortal((
    <>
      <div data-widget-chip
           ref={groupRef}
           className="position-absolute z-[201] flex items-center gap-1"
           style={{
             top: chipTop - layer.origin.top,
             left: box.right - 8 - layer.origin.left,
             transform: 'translate(-100%, -50%)',
           }}>
        {status && query && (
          <SimpleTooltip tooltip={props.queryOpen ? "Close the query" : `SPARQL · ${query.long}\nEdit the query`}>
            <button type="button"
                    aria-expanded={props.queryOpen}
                    onClick={() => {
                      props.onPick()
                      if (props.queryOpen) closeQueryOverlay()
                      else openQueryOverlay(props.path)
                    }}
                    className={mergeClasses(CHIP, CHIP_STYLE[status.status], {
                      // filled while the query is open on top of this widget
                      [CHIP_OPEN]: props.queryOpen && status.status === "success",
                    })}>
              <Braces size={11} aria-hidden />
              {compact ? <span className="sr-only">SPARQL</span> : `SPARQL · ${query.short}`}
            </button>
          </SimpleTooltip>
        )}
        {!props.settingsInPanel && (
          <SimpleTooltip tooltip={props.settingsOpen ? "Close the settings" : "Edit the widget's settings"}>
            <button type="button"
                    aria-expanded={props.settingsOpen}
                    onClick={() => {
                      props.onPick()
                      if (props.settingsOpen) {
                        hideSettingsOnWidget()
                        useWidgetSelectorStore.setState({ selectedPath: null })
                      } else {
                        openWidgetSettings(props.path)
                      }
                    }}
                    className={mergeClasses(CHIP, CHIP_STYLE.success, { [CHIP_OPEN]: props.settingsOpen })}>
              <Settings2 size={11} aria-hidden />
              {compact ? <span className="sr-only">Settings</span> : "Settings"}
            </button>
          </SimpleTooltip>
        )}
      </div>
    </>
  ), layer.container)
}

/** Explains the chips until dismissed, once per browser */
function QueryChipsHint() {
  const dismissed = useLocalSettings(s => s.queryChipsHintDismissed)
  if (dismissed) return null
  return createPortal((
    <div role="note"
         className="fixed bottom-4 left-4 z-[201] flex items-center gap-2 rounded-md border-[1px] border-border bg-white0 py-1 pl-2.5 pr-1 font-sans text-xs text-muted-foreground shadow-sm">
      <span>
        Pick a widget for its <Braces size={11} className="inline" aria-hidden /> SPARQL and Settings chips
      </span>
      <MiniButton title="Got it" aria-label="Dismiss" onClick={() => useLocalSettings.setState({ queryChipsHintDismissed: true })}>
        <X size={12} />
      </MiniButton>
    </div>
  ), document.body)
}

function visiblePart(rect: { top: number, left: number, width: number, height: number }, visible: VisibleBox): VisibleBox | null {
  const box = {
    top: Math.max(rect.top, visible.top),
    left: Math.max(rect.left, visible.left),
    right: Math.min(rect.left + rect.width, visible.right),
    bottom: Math.min(rect.top + rect.height, visible.bottom),
  }
  return box.right > box.left && box.bottom > box.top ? box : null
}

function describe(status: WidgetQueryStatus): { short: string, long: string } {
  switch (status.status) {
    case "success":
      return { short: `${status.rowCount ?? 0} rows`, long: `${status.rowCount ?? 0} rows` }
    case "pending":
      return { short: "running…", long: "Running" }
    case "error":
      return { short: "error", long: `Error: ${status.error}` }
    case "disabled":
      return { short: "waiting", long: "Not running: waiting for placeholder values" }
    default:
      return assertNever(status.status)
  }
}

/** Every widget reports a status, also layout widgets and others without a query */
function hasQuery(status: WidgetQueryStatus | undefined) {
  return !!status?.template
}
